import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertTriangle, ArrowLeft, Gift, Keyboard, Link2, QrCode, RefreshCw, SwitchCamera, UserPlus } from 'lucide-react'
import { HOOFDADRES } from '../lib/adres'
import { ApiFout, foutTekst, vraag, zetApparaat } from '../lib/api'
import { punten } from '../lib/opmaak'
import type { Beloning, Openbaar, Voucher } from '../lib/typen'
import { geldigEmail, normaliseerCode } from '../../supabase/functions/_gedeeld/regels.ts'
import { Lade } from '../onderdelen/Lade'
import { Installeren } from '../onderdelen/Installeren'
import { QrVlak } from '../onderdelen/KaartQr'
import { Logo } from '../onderdelen/Logo'
import { PinPad } from '../onderdelen/PinPad'
import { Scanner } from '../onderdelen/Scanner'
import { ontgrendel, speel } from './geluid'
import { useSchermAan } from './kiosk'
import { Klantscherm, type Klantstand } from './Klantscherm'
import { Medewerkersmenu } from './Medewerkersmenu'
import { bewaarMenu, leesMenu, Thuis, type MenuKnop } from './Thuis'

/*
 * De locatie-tablet. Het beginscherm heeft twee knoppen: Spaar (scannen
 * telt een wasbeurt) en Punten verzilveren (scannen zonder wasbeurt, meteen
 * naar de beloningen). Een klant kiest een beloning; een medewerker tikt zijn
 * PIN. De klant bevestigt nooit zelf een afschrijving (CLAUDE.md, kernregels).
 *
 * Elk scherm gaat vanzelf terug naar het begin, zodat de volgende chauffeur
 * nooit het saldo van de vorige ziet. De camera staat alleen aan op het
 * scanscherm.
 */

interface Stand { bestaat: boolean; gekoppeld?: boolean; naam?: string; melding?: string; geblokkeerd?: boolean; beloningen?: Beloning[]; fotos?: string[] }

type Modus = 'spaar' | 'verzilver'
type Keuze = { soort: 'inwisselen'; code: string; beloning: Beloning } | { soort: 'voucher'; code: string; voucher: Voucher }
type Ruw = Omit<Klantstand, 'status'> & { status: string; over?: number }

type Scherm =
  | { s: 'thuis' }
  | { s: 'scannen'; modus: Modus }
  | { s: 'bezig' }
  | { s: 'klant'; u: Klantstand }
  | { s: 'onbekend'; code: string }
  | { s: 'melding'; titel: string; tekst: string; soort: 'fout' | 'let' }

/** Seconden tot de tablet vanzelf teruggaat naar het begin (elke aanraking begint opnieuw). */
const TERUG_NA = { klant: 45, ingewisseld: 15, melding: 10, scannen: 60, onbekend: 60 }
const CAMERA = 'tp-tablet-camera'

export function Tablet({ slug, data }: { slug: string; data: Openbaar | null }) {
  const [stand, zetStand] = useState<Stand | null>(null)
  const [scherm, zetScherm] = useState<Scherm>({ s: 'thuis' })
  const [typen, zetTypen] = useState<Modus | null>(null)
  const [pinVoor, zetPinVoor] = useState<Keuze | null>(null)
  const [regBezig, zetRegBezig] = useState(false)
  const [regFout, zetRegFout] = useState<string | null>(null)
  const [pinFout, zetPinFout] = useState<string | null>(null)
  const [pinBezig, zetPinBezig] = useState(false)
  const [feest, zetFeest] = useState(false)
  const [tik, zetTik] = useState(0)
  const [cameraAan, zetCameraAan] = useState(() => { try { return localStorage.getItem(CAMERA) !== 'uit' } catch { return true } })
  const [cameraMelding, zetCameraMelding] = useState<string | null>(null)
  const [voorkant, zetVoorkant] = useState(() => { try { return localStorage.getItem(CAMERA + '-voor') === '1' } catch { return false } })
  const [menu, zetMenu] = useState(false)
  const [knoppen, zetKnoppen] = useState<MenuKnop[]>(leesMenu)
  const [toonBeloningen, zetToonBeloningen] = useState(false)
  const [toonTelefoon, zetToonTelefoon] = useState(false)
  const schermAan = useSchermAan()
  const sluitMenu = useCallback(() => zetMenu(false), [])
  const camera = (aan: boolean) => { zetCameraAan(aan); zetCameraMelding(null); try { localStorage.setItem(CAMERA, aan ? 'aan' : 'uit') } catch { /* */ } }
  const wisselCamera = () => { const n = !voorkant; zetVoorkant(n); try { localStorage.setItem(CAMERA + '-voor', n ? '1' : '0') } catch { /* */ } }
  const knoppenZetten = (m: MenuKnop[]) => { zetKnoppen(m); bewaarMenu(m) }
  const naarBegin = useCallback(() => { zetScherm({ s: 'thuis' }); zetFeest(false); zetTypen(null) }, [])

  const laadStand = useCallback(async () => {
    try { zetStand(await vraag<Stand>('tablet', { slug })) } catch (e) { zetStand({ bestaat: true, gekoppeld: false, melding: foutTekst(e) }) }
  }, [slug])
  useEffect(() => { laadStand() }, [laadStand])
  /* Nieuwe foto's en beloningen zonder herladen: elk half uur, en meteen bij terugkomen op het begin. */
  useEffect(() => { const t = setInterval(laadStand, 30 * 60_000); return () => clearInterval(t) }, [laadStand])

  useEffect(() => {
    document.documentElement.classList.add('altijd-donker')
    return () => { document.documentElement.classList.remove('altijd-donker') }
  }, [slug])

  /* Vanzelf terug naar het begin. Elke tik verlengt. */
  useEffect(() => {
    if (scherm.s === 'thuis' || scherm.s === 'bezig' || pinVoor) return
    const sec = scherm.s === 'klant' ? (scherm.u.status === 'ingewisseld' ? TERUG_NA.ingewisseld : TERUG_NA.klant)
      : scherm.s === 'scannen' ? TERUG_NA.scannen : scherm.s === 'onbekend' ? TERUG_NA.onbekend : TERUG_NA.melding
    const t = setTimeout(naarBegin, sec * 1000)
    return () => clearTimeout(t)
  }, [scherm, pinVoor, tik, naarBegin])

  const fout = (e: unknown) => {
    if (e instanceof ApiFout && e.code === 'niet-gekoppeld') { zetApparaat(null); laadStand(); return }
    speel('fout')
    zetScherm({ s: 'melding', titel: 'Dat lukte niet', tekst: foutTekst(e), soort: 'fout' })
  }

  const scan = useCallback(async (invoer: string, modus: Modus) => {
    const code = normaliseerCode(invoer)
    zetTypen(null)
    if (!code) { speel('fout'); zetScherm({ s: 'melding', titel: 'Geen puntenkaart', tekst: 'Deze code herkennen we niet. Scan de QR-code op je kaart, of typ de letters en cijfers die erbij staan.', soort: 'let' }); return }
    zetScherm({ s: 'bezig' })
    try {
      const r = await vraag<Ruw>(modus === 'spaar' ? 'scan' : 'tablet-bekijk', { slug, code })
      if (r.status === 'onbekend') {
        if (modus === 'spaar') { speel('wacht'); zetScherm({ s: 'onbekend', code }); return }
        speel('wacht')
        zetScherm({ s: 'melding', titel: 'Nieuwe kaart', tekst: 'Deze kaart is nog niet geregistreerd, dus er staan nog geen punten op. Kies Spaar om hem te registreren.', soort: 'let' })
        return
      }
      if (r.status === 'ingetrokken') { speel('fout'); zetScherm({ s: 'melding', titel: 'Kaart ingetrokken', tekst: 'Deze kaart werkt niet meer. Vraag een medewerker om een nieuwe; je punten blijven bewaard.', soort: 'let' }); return }
      speel(r.status === 'bijgeschreven' ? 'punt' : 'wacht')
      zetScherm({ s: 'klant', u: { ...r, code: r.code ?? code } as Klantstand })
      if (r.chauffeurBereikt) zetFeest(true)
    } catch (e) { fout(e) }
  }, [slug])

  /* iOS speelt pas geluid na een aanraking of toets: bij elke aanraking opnieuw proberen. */
  useEffect(() => {
    document.addEventListener('pointerdown', ontgrendel)
    document.addEventListener('keydown', ontgrendel)
    return () => { document.removeEventListener('pointerdown', ontgrendel); document.removeEventListener('keydown', ontgrendel) }
  }, [])

  /* Een handscanner die als toetsenbord werkt: op het begin telt dat als Spaar. */
  useEffect(() => {
    let buf = ''
    let laatste = 0
    const k = (e: KeyboardEvent) => {
      if (typen || pinVoor || menu || (e.target as HTMLElement)?.tagName === 'INPUT') return
      if (scherm.s !== 'thuis' && scherm.s !== 'scannen') return
      const nu = Date.now()
      if (nu - laatste > 120) buf = ''
      laatste = nu
      if (e.key === 'Enter') { if (buf.length >= 6) scan(buf, scherm.s === 'scannen' ? scherm.modus : 'spaar'); buf = ''; return }
      if (e.key.length === 1) buf += e.key
    }
    document.addEventListener('keydown', k)
    return () => document.removeEventListener('keydown', k)
  }, [scan, typen, pinVoor, menu, scherm])

  /* De klant registreert zelf, met zijn e-mailadres (geen medewerker nodig). */
  const registreer = async (code: string, email: string, toestemming: boolean) => {
    zetRegBezig(true); zetRegFout(null)
    try {
      const r = await vraag<Ruw>('activeren', { slug, code, email, toestemming })
      speel('punt')
      zetScherm({ s: 'klant', u: { ...r, code } as Klantstand })
    } catch (e) {
      if (e instanceof ApiFout && (e.code === 'niet-gekoppeld' || e.status >= 500)) fout(e)
      else zetRegFout(foutTekst(e))
    } finally { zetRegBezig(false) }
  }

  const pinKlaar = async (pin: string) => {
    if (!pinVoor) return
    zetPinBezig(true); zetPinFout(null)
    try {
      const r = pinVoor.soort === 'voucher'
        ? await vraag<Ruw>('voucher-inwisselen', { slug, code: pinVoor.code, voucher: pinVoor.voucher.id, pin })
        : await vraag<Ruw>('inwisselen', { slug, code: pinVoor.code, beloning: pinVoor.beloning.id, pin })
      if (r.status === 'pin-fout') { speel('fout'); zetPinFout(`Onjuiste PIN. Nog ${r.over} ${r.over === 1 ? 'poging' : 'pogingen'}.`); return }
      zetPinVoor(null)
      if (r.status === 'geblokkeerd') { zetStand((s) => s && { ...s, geblokkeerd: true }); zetScherm({ s: 'melding', titel: 'PIN geblokkeerd', tekst: 'Te vaak een onjuiste PIN. Inwisselen staat uit tot een manager het vrijgeeft in Beheer.', soort: 'fout' }); return }
      if (r.status === 'uitverkocht') { zetScherm({ s: 'melding', titel: 'Uitverkocht', tekst: 'Deze beloning is helaas uitverkocht. Kies een andere.', soort: 'let' }); return }
      if (r.status === 'te-weinig') { zetScherm({ s: 'melding', titel: 'Niet genoeg punten', tekst: `Het saldo is ${punten(r.saldo)}.`, soort: 'let' }); return }
      speel('ingewisseld')
      zetScherm({ s: 'klant', u: { ...r, code: pinVoor.code } as Klantstand })
    } catch (e) { zetPinVoor(null); fout(e) } finally { zetPinBezig(false) }
  }

  const naamOpslaan = async (voornaam: string, achternaam: string) => {
    if (scherm.s !== 'klant') return
    try {
      const r = await vraag<Ruw>('tablet-naam', { slug, code: scherm.u.code, voornaam, achternaam })
      zetScherm({ s: 'klant', u: { ...scherm.u, profiel: r.profiel } })
    } catch (e) { throw new Error(foutTekst(e)) }
  }

  const nieuwsbrief = async (aan: boolean) => {
    if (scherm.s !== 'klant') return
    try {
      const r = await vraag<Ruw>('tablet-toestemming', { slug, code: scherm.u.code, aan })
      zetScherm({ s: 'klant', u: { ...scherm.u, profiel: r.profiel } })
    } catch (e) { throw new Error(foutTekst(e)) }
  }

  if (!stand) return <div className="kiosk"><div className="paneel midden" style={{ margin: 'auto', border: 0, background: 'none' }}><RefreshCw className="draai" size={40} /></div></div>
  if (!stand.bestaat) return <Onbekend slug={slug} />
  if (!stand.gekoppeld) return <Koppelen slug={slug} naam={stand.naam ?? slug} melding={stand.melding} klaar={laadStand} />

  const beloningen = stand.beloningen ?? data?.beloningen ?? []
  const lengte = data?.regels.pinLengte ?? 4
  const kop = <KioskKop naam={stand.naam ?? ''} openMenu={() => zetMenu(true)} />

  return (
    <div className="kiosk" onPointerDown={() => zetTik((t) => t + 1)} onKeyDown={() => zetTik((t) => t + 1)}>
      {scherm.s === 'thuis' && (
        <Thuis kop={kop} fotos={stand.fotos ?? []} geblokkeerd={!!stand.geblokkeerd}
          spaar={() => zetScherm({ s: 'scannen', modus: 'spaar' })} verzilver={() => zetScherm({ s: 'scannen', modus: 'verzilver' })}
          menu={knoppen} instellingen={() => zetMenu(true)}
          kiesMenu={(k) => {
            if (k === 'beloningen') zetToonBeloningen(true)
            else if (k === 'vouchers') zetScherm({ s: 'scannen', modus: 'verzilver' })
            else if (k === 'code') zetTypen('spaar')
            else zetToonTelefoon(true)
          }} />
      )}

      {scherm.s !== 'thuis' && scherm.s !== 'klant' && kop}

      {scherm.s === 'scannen' && (
        <div className="kiosk-lijf">
          <section className="paneel midden">
            <h1>{scherm.modus === 'spaar' ? <>Scan je <span>kaart</span></> : <>Punten <span>verzilveren</span></>}</h1>
            <div className="scan-doel" aria-hidden={!cameraAan}>
              {cameraAan ? <Scanner gevonden={(t) => scan(t, scherm.modus)} voorkant={voorkant} kaal opFout={(r) => { zetCameraAan(false); zetCameraMelding(r) }} /> : <QrCode />}
            </div>
            <p className="uitleg">{cameraAan ? 'Houd de QR-code van je kaart voor de camera.' : 'Houd je kaart bij de scanner, of typ de code.'}
              {scherm.modus === 'verzilver' ? ' Je ziet meteen wat je kunt inwisselen.' : ''}</p>
            {cameraMelding && !cameraAan && <p className="stil" style={{ fontSize: 15 }}>{cameraMelding}</p>}
            <div className="rij" style={{ justifyContent: 'center' }}>
              <button className="knop" onClick={naarBegin}><ArrowLeft size={24} />Terug</button>
              {cameraAan && <button className="knop" onClick={wisselCamera}><SwitchCamera size={24} />Camera draaien</button>}
              <button className="knop" onClick={() => zetTypen(scherm.modus)}><Keyboard size={24} />Code typen</button>
            </div>
          </section>
        </div>
      )}

      {scherm.s === 'bezig' && (
        <div className="kiosk-lijf"><section className="paneel midden"><RefreshCw size={56} className="draai" /><p className="uitleg">Even kijken…</p></section></div>
      )}

      {scherm.s === 'klant' && (
        <Klantscherm u={scherm.u} geblokkeerd={!!stand.geblokkeerd} feest={feest} klaar={naarBegin}
          kies={(b) => { zetPinFout(null); zetPinVoor({ soort: 'inwisselen', code: scherm.u.code ?? '', beloning: b }) }}
          kiesVoucher={(v) => { zetPinFout(null); zetPinVoor({ soort: 'voucher', code: scherm.u.code ?? '', voucher: v }) }}
          sec={scherm.u.status === 'ingewisseld' ? TERUG_NA.ingewisseld : TERUG_NA.klant} tik={tik} nieuwsbrief={nieuwsbrief} naamOpslaan={naamOpslaan} />
      )}

      {scherm.s === 'onbekend' && (
        <Registreren code={scherm.code} bezig={regBezig} fout={regFout} terug={() => { zetRegFout(null); naarBegin() }}
          verder={(email, toestemming) => registreer(scherm.code, email, toestemming)} />
      )}

      {scherm.s === 'melding' && (
        <div className="kiosk-lijf">
          <section className="paneel midden">
            <AlertTriangle size={64} color={scherm.soort === 'fout' ? '#f4685f' : '#f5b544'} />
            <h1>{scherm.titel}</h1>
            <p className="uitleg">{scherm.tekst}</p>
            <button className="knop geel groot" onClick={naarBegin}>Terug naar het begin</button>
          </section>
        </div>
      )}

      <Lade titel="Beloningen" open={toonBeloningen} sluit={() => zetToonBeloningen(false)} klasse="kiosk-lade" breed kinderen={
        <div className="beloningen-raster">
          {beloningen.map((b) => (
            <div key={b.id} className="beloning-knop" style={{ cursor: 'default' }} aria-disabled={b.uitverkocht}>
              {b.afbeelding ? <img className="beloning-foto" src={b.afbeelding} alt="" /> : <span className="beloning-icoon"><Gift size={28} /></span>}
              <span><b>{b.naam}</b><small>{b.punten} punten{b.type === 'digitaal' ? ' · digitaal' : ''}</small></span>
              <span className="pijl">{b.uitverkocht ? 'Uitverkocht' : ''}</span>
            </div>
          ))}
          <p className="stil" style={{ fontSize: 16, gridColumn: '1 / -1' }}>Inwisselen: tik op Punten verzilveren en scan je kaart; een medewerker bevestigt.</p>
        </div>
      } />
      <Lade titel="Je punten op je telefoon" open={toonTelefoon} sluit={() => zetToonTelefoon(false)} klasse="kiosk-lade" kinderen={
        <div className="kaart-qr">
          <QrVlak inhoud={`https://${HOOFDADRES}`} />
          <b style={{ fontSize: 22 }}>{HOOFDADRES}</b>
          <p className="stil">Scan met de camera van je telefoon en vul de code van je kaart in. Daar zie je je saldo, beloningen en vouchers, en vul je je verjaardag in.</p>
        </div>
      } />
      <Lade titel="Typ de code van je kaart" open={!!typen} sluit={() => zetTypen(null)} klasse="kiosk-lade boven" kinderen={typen && <CodeTypen klaar={(c) => scan(c, typen)} />} />
      <Lade titel="Medewerker bevestigt"
        open={!!pinVoor} sluit={() => zetPinVoor(null)} klasse="kiosk-lade pin-lade" kinderen={
          <div>
            <p style={{ textAlign: 'center', fontSize: 20, fontWeight: 800, margin: '0 0 4px' }}>{pinVoor ? (pinVoor.soort === 'voucher' ? pinVoor.voucher.naam : `${pinVoor.beloning.naam} · ${pinVoor.beloning.punten} punten`) : ''}</p>
            <p style={{ textAlign: 'center', color: '#a3b2ce', fontSize: 17 }}>Vul je PIN in</p>
            <PinPad lengte={lengte} bezig={pinBezig} fout={pinFout} klaar={pinKlaar} />
            {pinFout && <div className="melding fout" style={{ marginTop: 14 }}>{pinFout}</div>}
          </div>
        } />
      <Medewerkersmenu slug={slug} lengte={lengte} open={menu} sluit={sluitMenu} cameraAan={cameraAan} zetCameraAan={camera}
        wisselCamera={wisselCamera} schermAan={schermAan} knoppen={knoppen} zetKnoppen={knoppenZetten} />
      {feest && <Feest />}
    </div>
  )
}

/*
 * De kop. Het medewerkersmenu zit er verborgen in: anderhalve seconde op het
 * logo drukken, of vijf keer snel op de klok tikken. Een klant vindt het
 * niet per ongeluk, en zonder PIN gaat het toch niet open.
 */
function KioskKop({ naam, openMenu }: { naam: string; openMenu?: () => void }) {
  const [nu, zetNu] = useState(() => new Date())
  useEffect(() => { const t = setInterval(() => zetNu(new Date()), 15_000); return () => clearInterval(t) }, [])
  const druk = useRef<number | undefined>(undefined)
  const tikken = useRef<number[]>([])
  const begin = () => { if (openMenu) druk.current = window.setTimeout(openMenu, 1500) }
  const stop = () => window.clearTimeout(druk.current)
  const klokTik = () => {
    if (!openMenu) return
    const t = Date.now()
    tikken.current = [...tikken.current.filter((x) => t - x < 3000), t]
    if (tikken.current.length >= 5) { tikken.current = []; openMenu() }
  }
  return (
    <header className="kiosk-kop">
      <span onPointerDown={begin} onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop} onContextMenu={(e) => e.preventDefault()}
        style={{ touchAction: 'none', WebkitTouchCallout: 'none' } as React.CSSProperties}><Logo /></span>
      <span className="plek vul">TW1 Club · <b>{naam.replace(/^Truckwash\s*/i, '')}</b></span>
      <span className="kiosk-klok" onPointerDown={klokTik}>{nu.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' })}</span>
    </header>
  )
}

function CodeTypen({ klaar }: { klaar: (c: string) => void }) {
  const [code, zet] = useState('')
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { setTimeout(() => ref.current?.focus(), 50) }, [])
  return (
    <form className="formulier" onSubmit={(e) => { e.preventDefault(); klaar(code) }}>
      <input ref={ref} className="code-invoer" value={code} onChange={(e) => zet(e.target.value)} placeholder="bijv. C100000"
        autoCapitalize="characters" autoComplete="off" autoCorrect="off" spellCheck={false} maxLength={30} enterKeyHint="go" />
      <button className="knop geel groot breed" disabled={code.trim().length < 6}>Punten bekijken</button>
    </form>
  )
}

function Registreren({ code, terug, verder, bezig, fout }: {
  code: string; terug: () => void; verder: (email: string, toestemming: boolean) => void; bezig: boolean; fout: string | null
}) {
  const [email, zetEmail] = useState('')
  /* Standaard aan: een klant die bij een wasbeurt zijn e-mail geeft, mag aanbiedingen krijgen zolang hij kan weigeren (hier afvinken, of afmelden in elke mail). */
  const [ja, zetJa] = useState(true)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { setTimeout(() => ref.current?.focus(), 80) }, [])
  const goed = geldigEmail(email.trim().toLowerCase())
  return (
    <div className="kiosk-lijf">
      <form className="paneel registreren" style={{ maxWidth: 980, width: '100%', margin: '0 auto' }}
        onSubmit={(e) => { e.preventDefault(); if (goed && !bezig) verder(email.trim(), ja) }}>
        <div className="registreren-kop">
        <UserPlus size={48} color="#f8c010" />
        <h1>Nieuwe <span>kaart</span></h1>
        <p className="uitleg">Kaart <b className="code-pil" style={{ color: '#fff' }}>{code}</b> is nog niet geregistreerd. Vul je e‑mailadres in en je spaart meteen: deze wasbeurt telt al.</p>
        </div>
        <div className="registreren-velden">
        <label className="veld">
          <span style={{ color: '#a3b2ce', fontSize: 16 }}>Je e-mailadres</span>
          <input ref={ref} className="invoer" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            value={email} onChange={(e) => zetEmail(e.target.value)} placeholder="jij@voorbeeld.nl" required enterKeyHint="go"
            onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)}
            style={{ minHeight: 64, fontSize: 20, background: '#0b1220', borderColor: '#2a3c5f', color: '#fff' }} />
        </label>
        <label className="vink groot-vink">
          <input type="checkbox" checked={ja} onChange={(e) => zetJa(e.target.checked)} />
          <span>Ja, Truckwash 1 Group mag mij aanbiedingen mailen. Afmelden kan altijd.</span>
        </label>
        {fout && <div className="melding fout">{fout}</div>}
        <div className="registreren-knoppen">
          <button type="button" className="knop groot" onClick={terug}>Annuleren</button>
          <button className="knop groot geel" disabled={!goed || bezig}>{bezig ? 'Even geduld…' : 'Registreren en sparen'}</button>
        </div>
        {!goed && email.trim().length > 3 && <p className="stil" style={{ fontSize: 16 }}>Vul een geldig e-mailadres in, bijv. jan@gmail.com</p>}
        </div>
      </form>
    </div>
  )
}

function Koppelen({ slug, naam, melding, klaar }: { slug: string; naam: string; melding?: string; klaar: () => void }) {
  const [code, zet] = useState('')
  const [bezig, zetBezig] = useState(false)
  const [fout, zetFout] = useState<string | null>(null)
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document)
  const losStaand = window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true
  const koppel = async () => {
    zetBezig(true); zetFout(null)
    try {
      const r = await vraag<{ token: string }>('tablet-koppelen', { slug, code, naam: `Tablet ${naam}` })
      zetApparaat(r.token)
      klaar()
    } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }
  return (
    <div className="kiosk">
      <KioskKop naam={naam} />
      <div className="kiosk-lijf">
        <section className="paneel" style={{ maxWidth: 640, width: '100%', margin: '0 auto' }}>
          <Link2 size={48} color="#f8c010" />
          <h1>Tablet <span>koppelen</span></h1>
          <p className="uitleg">Een manager maakt in Beheer → Tablets een koppelcode voor {naam.replace(/^Truckwash\s*/i, '')}. Die is 15 minuten geldig.</p>
          {melding && <div className="melding let">{melding}</div>}
          {ios && !losStaand && (
            <div className="melding info">Op een iPad: zet deze pagina eerst op het beginscherm (Deel → Zet op beginscherm), open hem vandaar en koppel dan. Anders onthoudt het icoon de koppeling niet.</div>
          )}
          <form className="formulier" onSubmit={(e) => { e.preventDefault(); koppel() }}>
            <input className="code-invoer" value={code} onChange={(e) => zet(e.target.value)} placeholder="ABCD-EFGH" autoCapitalize="characters"
              autoComplete="off" autoCorrect="off" spellCheck={false} maxLength={9} />
            {fout && <div className="melding fout">{fout}</div>}
            <button className="knop geel groot breed" disabled={bezig || code.replace(/[^a-z0-9]/gi, '').length !== 8}>{bezig ? 'Koppelen…' : 'Koppelen'}</button>
          </form>
          <Installeren naam={`TW1 Club ${naam.replace(/^Truckwash\s*/i, '')}`} tekst="Eerst als app op deze tablet zetten" klasse="knop groot breed" donker />
        </section>
      </div>
    </div>
  )
}

function Onbekend({ slug }: { slug: string }) {
  return (
    <div className="kiosk">
      <div className="kiosk-lijf"><section className="paneel midden">
        <h1>Geen vestiging <span>/{slug}</span></h1>
        <a className="knop geel groot" href="/">Naar TW1 Club</a>
      </section></div>
    </div>
  )
}

function Feest() {
  const kleuren = ['#f8c010', '#35d07f', '#58b6f5', '#ffffff', '#f4685f']
  return (
    <div className="feest" aria-hidden>
      {Array.from({ length: 80 }, (_, i) => (
        <i key={i} style={{
          left: `${(i * 37) % 100}%`, background: kleuren[i % kleuren.length],
          animationDuration: `${2.4 + (i % 7) * 0.35}s`, animationDelay: `${(i % 10) * 0.12}s`,
        }} />
      ))}
    </div>
  )
}
